// Cloud-admin data table + page header — a faithful copy of the operator app's
// `apps/web/src/components/ui/DataTable.tsx` so 5174 and 5173 render tables and
// page headers identically. cloud-admin can't import across app src roots (its
// @/* resolves only into its own src), so the primitive is mirrored here.
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Skeleton, EmptyState } from '@/components/ui';

export interface Column<T> {
  key: string;
  header: ReactNode;
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  className?: string;
}

export function DataTable<T extends { id: string }>({
  columns, rows, loading, emptyIcon, emptyTitle, emptyHint, onRowClick,
}: {
  columns: Column<T>[];
  rows: T[] | undefined;
  loading?: boolean;
  emptyIcon: LucideIcon;
  emptyTitle: string;
  emptyHint?: string;
  onRowClick?: (row: T) => void;
}) {
  const align = (a?: string) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');
  return (
    <div className="tk-card overflow-x-auto p-0">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-10 text-xs text-muted backdrop-blur">
          <tr className="border-b border-white/10">
            {columns.map((c) => (
              <th key={c.key} className={`px-4 py-3 font-medium ${align(c.align)}`}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && Array.from({ length: 5 }).map((_, i) => (
            <tr key={i} className="border-b border-white/5">
              <td className="px-4 py-3" colSpan={columns.length}><Skeleton className="h-5" /></td>
            </tr>
          ))}
          {!loading && (rows?.length ?? 0) === 0 && (
            <tr><td colSpan={columns.length}><EmptyState icon={emptyIcon} title={emptyTitle} hint={emptyHint} /></td></tr>
          )}
          {!loading && rows?.map((row) => (
            <tr
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={`border-b border-white/5 transition-colors hover:bg-white/5 ${onRowClick ? 'cursor-pointer' : ''}`}
            >
              {columns.map((c) => (
                <td key={c.key} className={`px-4 py-3 ${align(c.align)} ${c.className ?? ''}`}>
                  {c.render ? c.render(row) : (row as Record<string, unknown>)[c.key] as ReactNode}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="flex items-end justify-between">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
